import { useNavigate } from "react-router";
import { HeartPulse, Target, Users } from "lucide-react";
import type * as API from "@shared-core/data/api-types";
import { formatCurrencyGbpCompact } from "@shared-core/domain/formatters";
import { Card, CardContent } from "../../primitives/card";
import { Badge } from "../../primitives/badge";
import { IntentCardHeader } from "./IntentCardPrimitives";
import { CustomerCardHeader } from "./CustomerCardHeader";
import {
  COVERAGE_TONE,
  coverageBadge,
  HEALTH_TIER_LABEL,
  HEALTH_TIER_VARIANT,
  healthScoreFromFactorScores,
  healthScoreTier,
  toneFromVariant,
  untappedTier,
  type BadgeVariant,
  type HealthTierKey,
} from "./strategyTiers";

interface CustomerStrategyRowCardProps {
  customer: API.CustomerIntelligenceListItem;
  href?: string;
  onAddTicket?: (customerId: string) => void;
}

/**
 * Strategy-view row. Reuses the shared ``CustomerCardHeader`` so pulse
 * and strategy lenses share one top bar; below it renders three
 * minimal sub-cards mirroring the detail-page strategy pyramid — hero
 * + tier badge only, no factor lists or footer stats.
 */
export function CustomerStrategyRowCard({
  customer,
  href,
  onAddTicket,
}: CustomerStrategyRowCardProps) {
  const navigate = useNavigate();
  const strategy = customer.strategy_summary ?? null;

  const handleCardClick = () => {
    if (href) navigate(href);
  };

  const handleCardKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleCardClick();
    }
  };

  const untapped = strategy?.untapped_gbp ?? null;
  const untappedTierInfo = untappedTier(untapped);

  const coverage = (strategy?.coverage ?? null) as API.CoverageLevel | null;
  const coverageTone = coverage ? COVERAGE_TONE[coverage] : "muted";
  const coverageTierInfo = coverageBadge(coverage) ?? {
    label: "Unknown",
    variant: "neutral" as BadgeVariant,
  };
  const relationshipHeroCount = strategy?.strategic_key_contacts_count ?? 0;

  // Recompute the composite score locally so the card hero matches the
  // single source of truth (``ACCOUNT_HEALTH_FACTORS`` weights), even if
  // the backend rolls its expression forward. Backend sends the same
  // value on ``strategy.health_score`` for ORDER BY parity.
  const healthScore = healthScoreFromFactorScores(
    strategy?.account_health_factor_scores,
  );
  const isHealthOverridden = strategy?.health_tier_is_overridden ?? false;
  const overrideTierKey =
    isHealthOverridden && strategy?.health_tier
      ? (strategy.health_tier as HealthTierKey)
      : undefined;
  const healthTier = overrideTierKey
    ? {
        label: HEALTH_TIER_LABEL[overrideTierKey],
        variant: HEALTH_TIER_VARIANT[overrideTierKey],
      }
    : healthScoreTier(healthScore);
  const healthCardTone = toneFromVariant(healthTier?.variant);

  return (
    <Card
      className="transition-colors hover:bg-muted/50 cursor-pointer"
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role={href ? "link" : undefined}
      tabIndex={href ? 0 : undefined}
    >
      <CardContent className="p-5 space-y-4">
        <CustomerCardHeader
          customer={customer}
          href={href}
          onAddTicket={onAddTicket}
        />

        {/* Three strategy sub-cards. Matches the detail page pyramid
            layout so the two surfaces feel like one system. */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Opportunity */}
          <Card className="p-4 shadow-sm">
            <IntentCardHeader
              label="Opportunity"
              hero={
                untapped != null ? (
                  <span>
                    {formatCurrencyGbpCompact(untapped)}{" "}
                    <span className="text-base text-muted-foreground font-normal">
                      untapped
                    </span>
                  </span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )
              }
              badge={
                untappedTierInfo ? (
                  <Badge variant={untappedTierInfo.variant}>
                    {untappedTierInfo.label}
                  </Badge>
                ) : (
                  <Badge variant="neutral">Unknown</Badge>
                )
              }
              icon={<Target className="size-5" />}
              tone="primary"
            />
          </Card>

          {/* Relationship depth */}
          <Card className="p-4 shadow-sm">
            <IntentCardHeader
              label="Relationship depth"
              hero={
                <span>
                  {relationshipHeroCount}{" "}
                  <span className="text-base text-muted-foreground font-normal">
                    strategic &amp; key contacts
                  </span>
                </span>
              }
              badge={
                <Badge variant={coverageTierInfo.variant}>
                  {coverageTierInfo.label}
                </Badge>
              }
              icon={<Users className="size-5" />}
              tone={coverageTone}
            />
          </Card>

          {/* Account health */}
          <Card className="p-4 shadow-sm">
            <IntentCardHeader
              label="Account health"
              hero={
                <span>
                  {healthScore != null ? `${healthScore}%` : "—"}{" "}
                  <span className="text-base text-muted-foreground font-normal">
                    health score
                  </span>
                </span>
              }
              badge={
                healthTier ? (
                  <Badge
                    variant={healthTier.variant}
                    title={
                      isHealthOverridden
                        ? `${healthTier.label} (manually overridden)`
                        : undefined
                    }
                  >
                    {healthTier.label}
                    {isHealthOverridden ? (
                      <span aria-hidden="true"> ✎</span>
                    ) : null}
                  </Badge>
                ) : (
                  <Badge variant="neutral">Unknown</Badge>
                )
              }
              icon={<HeartPulse className="size-5" />}
              tone={healthCardTone}
            />
          </Card>
        </div>
      </CardContent>
    </Card>
  );
}
